import { useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CreateObjectFieldCard } from 'entities/kyrgyzstan/ui/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import type { ApiFieldErrorMap } from 'shared/lib/form'
import { applyApiFieldErrors } from 'shared/lib/form'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { InputWithLabel } from 'shared/ui/input-label'
import { SelectWithLabel } from 'shared/ui/select-label'
import { TextareaWithLabel } from 'shared/ui/textarea-label'

import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { ConditionsStepSkeleton } from './ui/conditions-step-skeleton'

import {
  CREATE_OBJECT_CONDITIONS_TEXT_MAX_LENGTH,
  createObjectConditionsFormDefaultValues,
  createObjectConditionsFormResolver,
  mapCreateObjectRulesResponseToFormValues,
} from './lib/conditions-step-form'
import type { CreateObjectConditionsFormValues } from './lib/conditions-step-form'
import {
  CREATE_OBJECT_TIME_INPUT_MAX_LENGTH,
  formatCreateObjectTimeInput,
} from './lib/time-input-mask'
import s from './styles.module.scss'

type ConditionsStepOperation = 'idle' | 'loading' | 'submitting'

interface CancellationOption {
  value: string
  name: string
}

const conditionsStepApiFieldMap: ApiFieldErrorMap<CreateObjectConditionsFormValues> =
  {
    arrival_time_from: 'arrivalTimeFrom',
    arrival_time_to: 'arrivalTimeTo',
    departure_time_before: 'departureTimeBefore',
    free_cancellation_minutes: 'freeCancellationMinutes',
    home_rules_text: 'homeRulesText',
  }

export const ConditionsStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [operation, setOperation] = useState<ConditionsStepOperation>('idle')
  const [cancellationOptions, setCancellationOptions] = useState<
    CancellationOption[]
  >([])
  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<CreateObjectConditionsFormValues>({
    defaultValues: createObjectConditionsFormDefaultValues,
    mode: 'onChange',
    resolver: createObjectConditionsFormResolver,
  })

  useEffect(() => {
    const abortController = new AbortController()

    const fetchRules = async () => {
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getRulesStep(
          draftId,
          abortController.signal,
        )

        setCancellationOptions(
          data.data.free_cancellation_options.map((option) => ({
            value: String(option.value),
            name: option.label,
          })),
        )
        reset(mapCreateObjectRulesResponseToFormValues(data.data))
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchRules()

    return () => {
      abortController.abort()
    }
  }, [draftId, reset, showApiErrorDialog])

  const handleSubmitConditions = async (
    formValues: CreateObjectConditionsFormValues,
  ) => {
    if (formValues.freeCancellationMinutes === null) {
      return
    }

    setOperation('submitting')

    try {
      await KyrgyzstanCreateObjectApi.saveRulesStep(draftId, {
        check_in_from: formValues.arrivalTimeFrom,
        check_in_until: formValues.arrivalTimeTo,
        check_out_until: formValues.departureTimeBefore,
        owner_notes: formValues.homeRulesText,
        free_cancellation_minutes: formValues.freeCancellationMinutes,
      })

      onNext()
    } catch (error) {
      setOperation('idle')

      if (!applyApiFieldErrors(error, setError, conditionsStepApiFieldMap)) {
        showApiErrorDialog(error)
      }
    }
  }

  const handleContinue = () => {
    if (operation !== 'idle') {
      return
    }

    void handleSubmit(handleSubmitConditions)()
  }

  const isOperationActive = operation !== 'idle'
  const isEditModePristine = mode === 'edit' && !isDirty
  const isContinueDisabled = !isValid || isOperationActive || isEditModePristine

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isContinueDisabled}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.conditions.title')}
    >
      <div className={clsx(s.container, 'container')}>
        {operation === 'loading' ? (
          <ConditionsStepSkeleton />
        ) : (
          <>
            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.conditions.arrival',
              )}
            >
              <Controller
                control={control}
                name="arrivalTimeFrom"
                render={({ field }) => (
                  <InputWithLabel
                    {...field}
                    variant="secondary"
                    type="text"
                    inputMode="numeric"
                    maxLength={CREATE_OBJECT_TIME_INPUT_MAX_LENGTH}
                    autoComplete="off"
                    label={t(
                      'kyrgyzstan_block.create_object.steps.conditions.arrival_from',
                    )}
                    disabled={isOperationActive}
                    error={errors.arrivalTimeFrom}
                    onChange={(event) =>
                      field.onChange(
                        formatCreateObjectTimeInput(event.target.value),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name="arrivalTimeTo"
                render={({ field }) => (
                  <InputWithLabel
                    {...field}
                    variant="secondary"
                    type="text"
                    inputMode="numeric"
                    maxLength={CREATE_OBJECT_TIME_INPUT_MAX_LENGTH}
                    autoComplete="off"
                    label={t(
                      'kyrgyzstan_block.create_object.steps.conditions.arrival_to',
                    )}
                    disabled={isOperationActive}
                    error={errors.arrivalTimeTo}
                    onChange={(event) =>
                      field.onChange(
                        formatCreateObjectTimeInput(event.target.value),
                      )
                    }
                  />
                )}
              />
            </CreateObjectFieldCard>

            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.conditions.departure',
              )}
            >
              <Controller
                control={control}
                name="departureTimeBefore"
                render={({ field }) => (
                  <InputWithLabel
                    {...field}
                    variant="secondary"
                    type="text"
                    inputMode="numeric"
                    maxLength={CREATE_OBJECT_TIME_INPUT_MAX_LENGTH}
                    autoComplete="off"
                    label={t(
                      'kyrgyzstan_block.create_object.steps.conditions.departure_from',
                    )}
                    disabled={isOperationActive}
                    error={errors.departureTimeBefore}
                    onChange={(event) =>
                      field.onChange(
                        formatCreateObjectTimeInput(event.target.value),
                      )
                    }
                  />
                )}
              />
            </CreateObjectFieldCard>

            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.conditions.cancel_conditions',
              )}
            >
              <Controller
                control={control}
                name="freeCancellationMinutes"
                render={({ field }) => (
                  <SelectWithLabel
                    key={`free-cancellation-${field.value ?? 'empty'}-${cancellationOptions.length}`}
                    variant="secondary"
                    label={t(
                      'kyrgyzstan_block.create_object.steps.conditions.cancel_conditions_placeholder',
                    )}
                    value={
                      field.value === null ? undefined : String(field.value)
                    }
                    options={cancellationOptions}
                    disabled={isOperationActive}
                    error={errors.freeCancellationMinutes}
                    onChange={(option) => field.onChange(Number(option.value))}
                  />
                )}
              />
            </CreateObjectFieldCard>

            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.conditions.house_rules',
              )}
            >
              <TextareaWithLabel
                {...register('homeRulesText')}
                variant="secondary"
                label={t(
                  'kyrgyzstan_block.create_object.steps.conditions.house_rules_placeholder',
                )}
                maxLength={CREATE_OBJECT_CONDITIONS_TEXT_MAX_LENGTH}
                showCounter
                disabled={isOperationActive}
                error={errors.homeRulesText}
              />
            </CreateObjectFieldCard>
          </>
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
